import React, { useEffect, useState } from 'react';
import { ContactsHeader } from '../../Contacts/subcomponents/Header';
import { ContactsRow } from '../../Contacts/subcomponents/Row';
import { useSearchParams, useParams } from 'react-router-dom';
import { ControlledPopup, EllipsisDropdown, Notification, RecordsTable, STATUS_TYPES, USER_PERMISSIONS } from 'dyl-components';
import { useSelector, useDispatch } from 'react-redux';
import { Dimmer, Dropdown, DropdownItem, Header, Loader, Popup } from 'semantic-ui-react';
import contactsActions from 'actions/contacts';
import QuoteBuilderProvider from 'shared/context/QuoteBuilderProvider';
import QuoteBuilderModal from 'shared/modals/QuoteBuilderModal';
import groupsActions from 'actions/groups';
import RestrictedView from 'shared/RestrictedView';
import MoveContactPopup from '../../Groups/subcomponents/MoveContactPopup';

const GroupContactsTable = ({ onFilter, onPageChange, parent }) => {
    const { group_id } = useParams();
    const dispatch = useDispatch();
    const { contacts, isReadingContacts, count, isDeletingGroup, user_id } = useSelector((state) => {
        return {
            user_id: state.auth.user_id,
            contacts: group_id === "hotlist" ? state.groups.hotlistContacts : group_id === "direct_customer" ? state.groups.directCustomerContacts : state.groups.contacts,
            isReadingContacts: state.groups.isReadingContacts,
            count: group_id === "hotlist" ? state.groups.hotlistCount || 0 : group_id === "direct_customer" ? state.groups.directCustomerCount || 0 : state.groups.contactCount || 0,
            isDeletingGroup: state.groups.isDeletingGroup
        };
    });

    const [params] = useSearchParams();

    const isColumnHidden = (column) => {
        return ["activity", "coowner_id"].includes(column);
    }
    useEffect(() => {
        dispatch(contactsActions.getOwnersAndCoOwners({
            list: group_id === "hotlist" ? "hotlist" : group_id === "direct_customer" ? "contact" : "group",
            list_query_on: group_id === "hotlist" || group_id === "direct_customer" ? user_id : group_id
        }))
    }, [dispatch, group_id, user_id])

    const [contactBeingConverted, setContactBeingConverted] = useState(null);

    const onSelectContactForConversion = (id) => {
        setContactBeingConverted(id);
    }

    const detailsOfContactBeingConverted = contacts.find(contact => contact.id === contactBeingConverted);

    const refresh = () => {
        const query = Object.fromEntries(params);
        if (group_id === "hotlist") {
            return dispatch(groupsActions.readHotlist({ ...query, limit: 25 }));
        } else if (group_id === "direct_customer") {
            return dispatch(groupsActions.readDirectCustomer({ ...query, limit: 25, direct_customer: true, fields: ['direct_customer', 'created', 'account'] }));
        }
        return dispatch(groupsActions.readGroupContacts(group_id, query));
    }

    const [groupContactBeingEdited, setGroupContactBeingEdited] = useState(null);
    const contactBeingRemovedFromGroup = useSelector(state => state.groups.contactBeingRemovedFromGroup)

    const onUnmarkAsDirectCustomer = async (contact_id) => {
        try {
            if (!contact_id) return;

            await dispatch(
                contactsActions.contactBulkAction({
                    action: "unset_direct_customer",
                    contact_id: [contact_id],
                }),
            );
            refresh();
            Notification.alert(
                "Successfully unmarked as Direct Customer",
                STATUS_TYPES.SUCCESS,
            );
        } catch (error) {
            Notification.alert(
                "Failed to unmark as Direct Customer",
                STATUS_TYPES.ERROR,
            );
        }
    };

    const onMarkAsDirectCustomer = async (contact_id) => {
        try {
            if (!contact_id) return;

            await dispatch(
                contactsActions.contactBulkAction({
                    action: "set_direct_customer",
                    contact_id: [contact_id],
                }),
            );
            refresh();
            Notification.alert(
                "Successfully marked as Direct Customer",
                STATUS_TYPES.SUCCESS,
            );
        } catch (error) {
            Notification.alert(
                "Failed to mark as Direct Customer",
                STATUS_TYPES.ERROR,
            );
        }
    };
    return (
        <>
            <Dimmer active={isDeletingGroup}>
                <Loader active={isDeletingGroup} />
            </Dimmer>
            <QuoteBuilderProvider refreshAccountInformation={refresh}>
                <RecordsTable
                    isTableLoading={isReadingContacts}
                    isSearchResultEmpty={count === 0 && (params.get('search') || params.get('start') || params.get('end') || params.get('account_type') || params.get('owned_by'))}
                    isTableEmpty={count === 0 && !(params.get('search') || params.get('start') || params.get('end') || params.get('account_type') || params.get('owned_by'))}
                    TableHeader={<ContactsHeader isInGroups isColumnHidden={isColumnHidden} onFilter={onFilter} />}
                    TableRows={contacts.map(contact => {
                        const isEditingContact = groupContactBeingEdited === contact.id;
                        return (
                            <ContactsRow
                                key={contact.id}
                                contact={contact}
                                isColumnHidden={isColumnHidden}
                                onSelectContactForConversion={() => onSelectContactForConversion(contact.id)}
                                refresh={refresh}
                                actions={(
                                    <>
                                        <Dimmer active={contactBeingRemovedFromGroup === contact.id}>
                                            <Loader active={contactBeingRemovedFromGroup === contact.id} />
                                        </Dimmer>
                                        <Popup
                                            trigger={<EllipsisDropdown {...isEditingContact ? { isDropdownOpen: false } : {}}>
                                                {[
                                                    ...(group_id !== 'direct_customer' ? [
                                                        {
                                                        id: 1,
                                                        text: 'Move contact',
                                                        icon: 'fa-solid fa-folder-arrow-down',
                                                        onClick: () => setGroupContactBeingEdited(contact.id)
                                                        },
                                                        {
                                                            id: 2,
                                                            text: parent?.name ? 'Remove from subgroup' : 'Remove from group',
                                                            icon: 'fa-solid fa-trash',
                                                            onClick: async () => {
                                                                try {
                                                                    if (group_id !== "hotlist") {
                                                                        await dispatch(groupsActions.deleteContactGroup(contact.id, { group_id }));
                                                                    } else {
                                                                        await dispatch(groupsActions.bulkHotlistContacts({ action: "delete_contacts", contact_id: [contact.id] }));
                                                                    }
                                                                    Notification.alert("Successfully removed contact from group!", STATUS_TYPES.SUCCESS);
                                                                    refresh();
                                                                } catch (e) {
                                                                    Notification.alert("Failed to remove contact from group", STATUS_TYPES.ERROR);
                                                                }
                                                            }
                                                        }
                                                    ] : []),
                                                    {
                                                        id: 3,
                                                        text: `${contact?.direct_customer ? 'Unmark as Direct Customer' : 'Mark as Direct Customer'}`,
                                                        icon: `fa-solid fa-crown`,
                                                        onClick: () => contact?.direct_customer ? onUnmarkAsDirectCustomer(contact?.id) : onMarkAsDirectCustomer(contact?.id)
                                                    },
                                                ].map(({ text, icon, disabled, onClick, ...otherProps }, idx) => {
                                                    return idx === 1 ? (
                                                        <RestrictedView type='controlledPopupDisabled' userPermission={USER_PERMISSIONS.CONTACTS_ADD}>
                                                            <ControlledPopup
                                                                header={<Header as='h4' textAlign='center'>
                                                                    Are you sure?
                                                                    <Header.Subheader style={{ marginTop: 5 }}>
                                                                        You cannot undo delete actions.
                                                                    </Header.Subheader>
                                                                </Header>}
                                                                trigger={<Dropdown.Item icon={icon} text={text} disabled={disabled} />}
                                                                onConfirm={onClick}
                                                                inverted 
                                                                />
                                                        </RestrictedView>
                                                    ) : (
                                                        <DropdownItem
                                                            key={idx}
                                                            text={text}
                                                            icon={icon}
                                                            disabled={disabled}
                                                            onClick={onClick}
                                                            {...otherProps} />
                                                    );
                                                })}
                                            </EllipsisDropdown>}
                                            content={<MoveContactPopup
                                                handlePopupClose={() => setGroupContactBeingEdited(null)}
                                                contact_id={contact.id}
                                                group_id={group_id} />}
                                            on='click'
                                            position='bottom left'
                                            open={isEditingContact}
                                            onClose={() => setGroupContactBeingEdited(null)}
                                            flowing />
                                    </>
                                )} />
                        );
                    })}
                    recordsCount={count}
                    activePage={params.get('page')}
                    onPageChange={onPageChange}
                    recordsName='Group Contact'
                    emptyListTitle="No Contacts Added"
                    emptySubtitleHidden
                />
                <QuoteBuilderModal 
                    contact_id={detailsOfContactBeingConverted?.id} 
                    account_id={detailsOfContactBeingConverted?.account?.id} 
                    isLinkedToCustomer={detailsOfContactBeingConverted?.account?.customer} 
                    pipeline_id={detailsOfContactBeingConverted?.module?.id} 
                />
            </QuoteBuilderProvider>
        </>
    )
}

export default GroupContactsTable;
